import {
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material';
import ListToolbar from './ListToolbar';
import ListCard from './ListCard';
import DetailCard from './DetailCard';
import useAppStore from '@/store/app';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

export default function ListView({
  rows,
  columns,
  actions,
  detailComponents,
  setSelectedRows = () => {},
  selectedRows = [],
  loading,
  refetch,
  filterModel,
  sortModel,
  columnVisibilityModel: externalModel,
  externalIds,
  livedata,
  onRowClick,
  headerFilters,
  columnSearch = false,
  loadSpecificItem = null,
}) {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const lg = useMediaQuery(theme.breakpoints.down('lg'));
  const { selectedItem, setSelectedItem } = useAppStore();
  const { pathname } = useLocation();
  const { id } = useParams();

  const [loadedSpecificItems, setLoadedSpecificItems] = useState([]);
  const [isLoadingSpecificItem, setIsLoadingSpecificItem] = useState(false);

  const combinedRows = [...rows, ...loadedSpecificItems];
  const detailData = combinedRows.find((row) => row._id === selectedItem);

  const [headerFiltersActive, setHeaderFiltersActive] =
    useState(!!headerFilters);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('toolsColumnVisibility');
    if (saved) return JSON.parse(saved);
    const obj = {};
    for (const col of columns) {
      obj[col.field] = !col.hidden;
    }
    return obj;
  });

  useEffect(() => {
    if (externalModel) setColumnVisibility(externalModel);
  }, [externalModel]);

  const handleColumnVisibilityChange = (model) => {
    setColumnVisibility(model);
    localStorage.setItem('toolsColumnVisibility', JSON.stringify(model));
  };

  const handleRowClick = (params, event) => {
    if (onRowClick) onRowClick(params, event);
    if (!detailComponents || detailComponents.length === 0) return;
    event.stopPropagation();
    const url = `${pathname.replace(id, '').replace(/\/$/, '')}/${params.row._id}`;
    window.history.pushState({}, '', url);
    setSelectedItem(params.row._id);
  };

  useEffect(() => {
    if (id) setSelectedItem(id);
  }, [id]);

  useEffect(() => {
    const loadSpecificItemIfNeeded = async () => {
      if (selectedItem && loadSpecificItem) {
        const itemExists = combinedRows.some((row) => row._id === selectedItem);

        if (!itemExists && !isLoadingSpecificItem) {
          setIsLoadingSpecificItem(true);
          try {
            const item = await loadSpecificItem(selectedItem);
            if (item) {
              setLoadedSpecificItems((prev) => {
                const exists = prev.some(
                  (existing) => existing._id === item._id,
                );
                return exists ? prev : [...prev, item];
              });
            }
          } catch (error) {
            console.error('Fehler beim Laden des spezifischen Items:', error);
          } finally {
            setIsLoadingSpecificItem(false);
          }
        }
      }
    };

    loadSpecificItemIfNeeded();
  }, [selectedItem, combinedRows, loadSpecificItem, isLoadingSpecificItem]);

  useEffect(() => {
    if (!selectedItem) {
    }
  }, [selectedItem]);

  const actualColumnVisibilityModel = sm
    ? Object.keys(columnVisibility).reduce((acc, key) => {
        acc[key] = key === 'title' || key === 'status';
        return acc;
      }, {})
    : columnVisibility;

  const finalActions = actions ? [...actions] : [];

  if (columnSearch) {
    const searchAction = (
      <Tooltip
        key="datagrid-search"
        title={
          headerFiltersActive
            ? 'Spaltensuche deaktivieren'
            : 'Spaltensuche aktivieren'
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color={headerFiltersActive ? 'primary' : 'gray'}
            onClick={() => setHeaderFiltersActive((prev) => !prev)}
          >
            <SearchOutlinedIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
    actions.length === 0 && finalActions.push(<Box sx={{ flexGrow: 1 }} />);
    finalActions.push(searchAction);
  }

  return (
    <Box
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {finalActions?.length > 0 && (
        <ListToolbar actions={finalActions} selectedRows={selectedRows} />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: lg ? 'column' : 'row',
          flex: 1,
          gap: 2,
          minHeight: 0,
        }}
      >
        {(!lg || !selectedItem) && (
          <ListCard
            rows={combinedRows}
            columns={columns}
            checkboxSelection={!!actions}
            loading={loading || isLoadingSpecificItem}
            setSelectedRows={setSelectedRows}
            selectedRows={selectedRows}
            refetch={refetch}
            externalFilterModel={filterModel}
            externalSortModel={sortModel}
            externalColumnVisibilityModel={actualColumnVisibilityModel}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            externalIds={externalIds}
            livedata={livedata}
            onRowClick={handleRowClick}
            sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}
            headerFilters={headerFiltersActive}
          />
        )}
        {selectedItem && detailComponents && detailData && (
          <DetailCard
            key={detailData._id}
            data={detailData}
            handleExit={() => {
              if (selectedItem)
                window.history.pushState(
                  {},
                  '',
                  pathname.replace(id, '').replace(/\/$/, ''),
                );
              setSelectedItem(null);
            }}
            components={detailComponents}
            sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}
          />
        )}
      </Box>
    </Box>
  );
}
